// Persistent agents — long-lived, mostly-idle agents that watch over and
// manage robots (and, later, other persistent agents). A persistent agent
// definition IS an `Agent` (of `kind: ORCHESTRATION`) plus a 1:1 extended
// config (`OrchestrationAgentConfig`, shared primary key — no independent
// "group" identity); a launched instance then sits IDLE until an EVENT wakes
// it and reads current world state from FACTS.
//
// Vocabulary (settled in design):
//   - PersistentAgentGroup — the definition: an ORCHESTRATION-kind Agent plus
//                            its extended config (role, defaults). Its `id` IS
//                            the backing Agent's id. Kept as the type/DTO name
//                            (and the `v1/persistent-agent-groups` route) to
//                            minimize consumer churn even though there's no
//                            separate "group" table anymore.
//                            "new persistent agent" in the UI creates one of these
//   - PersistentAgent      — a living instance (a durable Mastra thread)
//   - PersistentAgentEvent — "this happened" (append-only, wakes the agent)
//   - PersistentAgentFact  — "this is currently true" ({date,type,data} lane)
//
// Under the hood events map to Mastra messages/signals and facts map to Mastra
// state signals; that translation lives in @cortex/mastra-layer. These schemas
// are the cross-package source of truth (repo rule #3) — types are inferred.
//
// The autonomous "goal" loop is intentionally absent: it is not in the
// published @mastra/core build, so an agent only acts when an EVENT wakes it;
// its purpose is just an initial FACT. Autonomous self-driving lands when core
// ships the loop.
import { z } from 'zod';
import { MemoryPrioritySchema, MemoryStatusSchema, MemoryTypeSchema } from './agent-memory.js';
import { AgentSchema, CreateAgentInputSchema } from './agent.js';
import { MemoryAnchorSetSchema } from './memory-anchors.js';
import { ProviderFailureKindSchema } from './provider-failure.js';
import { RunProfileSchema } from './run-profile.js';
import { WatchConditionSchema } from './watch-detection.js';
import { WatchQuerySchema } from './watch-query.js';
import { parseWatchSelector } from './watch-selector.js';
// ─── Enums ───────────────────────────────────────────────────────────────
/**
 * Role of a persistent agent. For now there is a single kind — WORKER (operates
 * robots). The field is kept so additional roles (e.g. a supervisor that manages
 * other agents, with a loop-free supervisor→worker messaging rule) can be added
 * later without a schema reshape.
 */
export const PersistentAgentRoleSchema = z.enum(['WORKER']);
/**
 * Agent lifecycle. STARTING → (IDLE ⇄ WORKING)* → STOPPED. PAUSED suspends
 * waking; FAILED is a terminal error state the host could not recover.
 */
export const PersistentAgentStatusSchema = z.enum([
    'STARTING',
    'IDLE',
    'WORKING',
    'PAUSED',
    'WAITING_FOR_INPUT',
    'STOPPED',
    'FAILED',
]);
/**
 * The LIVE statuses — a thread in any of these is host-owned work: it must be
 * claimed by reconcile, stopped when its group is removed, and counted as
 * running. THE canonical list (DEV-16877): every status-enumeration site
 * consumes this so the next enum addition can't silently miss one.
 */
export const persistentAgentLiveStatuses = [
    'STARTING',
    'IDLE',
    'WORKING',
    'PAUSED',
    'WAITING_FOR_INPUT',
];
/**
 * Kind of an event — drives both routing intent and how the watch UI renders
 * the row. `instruction` = a human/supervisor directive; `notification` = an
 * external happening (sensor, webhook); `completion` = a command finished;
 * `wake` = a self-scheduled timer fired; `goal` = an objective was submitted
 * (autonomous goal loop) — logged only, never delivered as a wake.
 */
export const PersistentAgentEventKindSchema = z.enum([
    'instruction',
    'notification',
    'completion',
    'wake',
    'goal',
]);
/**
 * When an event reaches the agent. `now` → deliver into the active loop or wake
 * an idle thread (Mastra sendMessage / sendSignal ifIdle:wake); `interrupt` →
 * abort the active run and deliver immediately as the next turn (steering);
 * `next-turn` → wait for the active run, then a fresh turn
 * (queueMessage); `when-idle` → only wake if idle, else queue; `inbox` →
 * persist as a durable notification record without waking (ifIdle:persist).
 */
export const PersistentAgentEventDeliverySchema = z.enum([
    'now',
    'interrupt',
    'next-turn',
    'when-idle',
    'inbox',
]);
/** Visual type of a fact — drives the dashboard widget. Only 'text' today. */
export const PersistentAgentFactTypeSchema = z.enum(['text']);
/**
 * Host-read debug/experiment keys carried on a base agent's free-form `config`
 * (the same place `config.reflection` and `config.memory` live). Parsed
 * leniently at the host's single memory-injection decision point
 * (apps/persistent-agent-host runtime.ts): unknown keys are ignored, and a
 * malformed value is treated as the flag being absent.
 */
export const PersistentAgentDebugConfigSchema = z.object({
    /**
     * Debug/experiment flag: when true, the host skips resolving+injecting the
     * # Memory block for this thread's turns AND skips registering the
     * search_memories/save_memory tools. Used for memory-efficacy ablation
     * runs (and by the benchmark RunProfile `memoryMode: 'OFF'` arm); never
     * exposed in the console.
     */
    memoryInjectionDisabled: z.boolean().optional(),
});
/**
 * One step of a self-reported, multi-phase progress bar (the agent's
 * `set_progress` tool). `name` is always shown; `description` is the
 * longer explanation shown on hover over the phase's tick.
 */
export const PersistentAgentProgressPhaseSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(60),
    description: z.string().max(300).optional(),
});
/**
 * An agent's one self-reported progress bar: a short title describing the
 * overall plan, its ordered phases, and which one is current. An agent has
 * at most one — a second `set_progress` call replaces it outright.
 */
export const PersistentAgentProgressBarSchema = z.object({
    title: z.string().min(1).max(80),
    phases: z.array(PersistentAgentProgressPhaseSchema).min(1).max(20),
    currentPhaseId: z.string(),
});
/**
 * The `progress` fact lane's `data` shape — the agent's current progress bar,
 * or null once cleared (`clear_progress`). Wrapped in an object (rather than
 * storing the bar directly) so "no active bar" is still valid JSON for the
 * fact lane's non-nullable `data` column.
 */
export const PersistentAgentProgressFactSchema = z.object({
    progress: PersistentAgentProgressBarSchema.nullable(),
});
/**
 * The palette a thread may tint itself with (`set_status_color` tool) — a
 * traffic light, deliberately nothing more. Three states an operator can read
 * across a room without a legend; a richer palette only trades that glance for
 * a lookup. "No color" is the fourth state and is expressed by ABSENCE (null,
 * via `clear_status_color`), not by a member.
 *
 * A closed enum, NOT free-form CSS: each member maps to an existing design
 * token in the console's ui-kit (`packages/ui-kit/tokens.css`), so an agent can
 * never paint itself something illegible or off-brand. The console resolves
 * member → CSS variable (`apps/console/src/lib/status-color.ts`); this package
 * deliberately stays free of CSS.
 */
export const PersistentAgentStatusColorSchema = z.enum([
    /** Healthy — on plan, nothing needed. */
    'GREEN',
    /** Degraded — slow, retrying, or will need a human eventually. */
    'YELLOW',
    /** Failing or blocked — needs a human now. */
    'RED',
]);
/**
 * Hard cap on a color-shift reason. Deliberately small: the reason is a
 * glance-level caption under a colored dot on a wall monitor, not a log line.
 * Enforced identically by the tool's input schema and the log table.
 */
export const persistentAgentStatusColorReasonMaxChars = 120;
/**
 * Why a thread shifted color — one short clause, always required, on a clear
 * as much as on a set. The color alone says "something changed"; the reason is
 * what makes an operator's glance actionable, so the tools refuse a shift
 * without one.
 */
export const PersistentAgentStatusColorReasonSchema = z
    .string()
    .trim()
    .min(1)
    .max(persistentAgentStatusColorReasonMaxChars);
/**
 * One row of the durable color-shift log — an append-only trail of how a
 * thread's self-reported health moved over its lifetime, and why. A CLEAR
 * (`clear_status_color`) is a shift like any other and gets its own row, with
 * `color: null`.
 */
export const PersistentAgentStatusColorLogEntrySchema = z.object({
    id: z.string(),
    persistentAgentId: z.string(),
    /** The color the thread moved TO; null when the shift was a clear. */
    color: PersistentAgentStatusColorSchema.nullable(),
    /** The color it moved FROM; null when it was wearing none. */
    previousColor: PersistentAgentStatusColorSchema.nullable(),
    /** Short clause explaining the shift (max 120 chars). */
    reason: z.string(),
    createdAt: z.date(),
});
/**
 * One row of the LEGACY compaction log — an append-only record of every time
 * the retired hand-rolled compactor summarized a thread's transcript into its
 * rolling context block. Context compression is Mastra Observational Memory's
 * job now; no new rows are ever written, and `PersistentAgent.compactionSummary`
 * is frozen at whatever the last pre-switch compaction wrote (injected
 * read-only into that thread's prompt for continuity). Served by the
 * deprecated `GET .../compactions` so pre-switch history stays inspectable.
 */
export const PersistentAgentCompactionEntrySchema = z.object({
    id: z.string(),
    persistentAgentId: z.string(),
    /** The version this compaction WROTE (the row's `compactionVersion` after it). */
    version: z.number().int(),
    /** The full `<context_summary>` block as written. */
    summary: z.string(),
    /** Thread message count at compaction time — what `compactedAtMsgCount` became. */
    msgCount: z.number().int(),
    /** How many head messages were folded into the summary (the rest stayed verbatim). */
    summarizedCount: z.number().int(),
    /** True when an operator pressed "Compact now" rather than a threshold firing. */
    forced: z.boolean(),
    createdAt: z.date(),
});
/**
 * A live read of a thread's current Mastra Observational Memory (OM) state —
 * the engineer-facing "peek" the orchestration page's memory button shows.
 * Distinct from `PersistentAgentCompactionEntry` (frozen pre-OM history):
 * this reads live from Mastra's own tables (`mastra_threads.metadata` +
 * `mastra_observational_memory`) via `readObservationalMemorySnapshot` in
 * `@cortex/mastra-layer`, not from a cortex-owned projection, so it reflects
 * whatever the Observer last wrote — including nothing yet, on a thread too
 * young to have been observed.
 */
export const PersistentAgentObservationalMemorySnapshotSchema = z.object({
    /** The Mastra thread id (== the persistent agent id). */
    threadId: z.string(),
    /** The Mastra resourceId this thread's memory is scoped under (org/agent/thread). */
    resourceId: z.string(),
    workingMemory: z.object({
        /** The Observer-maintained working memory, exactly as stored (a JSON string), or null if never set. */
        raw: z.string().nullable(),
        /** `raw` parsed as JSON when it parses to an object; null otherwise (including when `raw` is null). */
        parsed: z.record(z.string(), z.unknown()).nullable(),
    }),
    /** Null when OM has never observed this thread yet (e.g. it just started). */
    observationalMemory: z
        .object({
        scope: z.enum(['thread', 'resource']),
        originType: z.enum(['initial', 'reflection']),
        /** Incremented each time a reflection re-condenses the log into a new generation. */
        generationCount: z.number().int(),
        /** The Observer's current compressed running summary. */
        activeObservations: z.string(),
        /** Cursor: messages at/before this time are folded into `activeObservations`. */
        lastObservedAt: z.date().nullable(),
        createdAt: z.date(),
        updatedAt: z.date(),
    })
        .nullable(),
});
// ─── Entities ──────────────────────────────────────────────────────────────
/** A user-facing collection of orchestration-agent definitions. */
export const OrchestrationGroupSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /** Per-user favorite marker. Older servers may omit it. */
    isFavorite: z.boolean().optional(),
    /** Number of live orchestration-agent definitions currently in the group. */
    agentCount: z.number().int().nonnegative(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const OrchestrationGroupRefSchema = OrchestrationGroupSchema.pick({
    id: true,
    name: true,
});
export const CreateOrchestrationGroupInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
});
export const UpdateOrchestrationGroupInputSchema = CreateOrchestrationGroupInputSchema.partial();
/** Clone the definitions/configuration in a group; live threads are never copied. */
export const CloneOrchestrationGroupInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
});
/**
 * A required fact lane declared on a group: every agent launched from the
 * group must supply a value for this lane at launch. Just a key + visual type
 * (only 'text' today) — the value is provided per agent as an initial fact.
 */
export const PersistentAgentRequiredFactSchema = z.object({
    /** Lane key the launcher must fill, e.g. "mission", "robot". */
    laneId: z.string().min(1).max(200),
    type: PersistentAgentFactTypeSchema.default('text'),
});
// ─── Peer grants (agent ↔ agent permissions) ─────────────────────────────
/**
 * Event kinds a peer grant may authorize. `goal` is excluded — goal events are
 * an internal logging kind (never delivered as a wake), so a peer can't emit one.
 */
export const PersistentAgentPeerEventKindSchema = z.enum([
    'instruction',
    'notification',
    'completion',
    'wake',
]);
/**
 * What one orchestration agent's threads may do to another agent's threads.
 * Absent/false capability = forbidden. `sendEvent` and `putFact` are scoped
 * further (which kinds×deliveries, which lanes) so a grant can allow "notify
 * politely, never steer" without allowing interrupts.
 */
export const PersistentAgentPeerCapabilitiesSchema = z.object({
    /** May enumerate the target agent's threads (id, name, status, required facts). */
    listThreads: z.boolean().default(false),
    /** May start new threads on the target agent (must supply its required fact lanes). */
    startThread: z.boolean().default(false),
    /**
     * May STOP the target agent's threads. One-way: STOPPED is terminal (only a
     * PAUSED thread can resume), so this is the most destructive capability —
     * grant it deliberately. The stopped thread's row/transcript/facts survive;
     * deletion stays human-only. Optional (not defaulted) so grants persisted
     * before this capability existed parse unchanged.
     */
    stopThread: z.boolean().optional(),
    /**
     * May READ the target agent's message log (list_peer_messages) — the
     * flattened transcript of what its threads have said and done, for analysis.
     * Read-only: it grants no way to write to, wake, or steer the target. Still
     * a real disclosure (transcripts can carry anything the target was told), so
     * it is its own capability rather than riding along with listThreads.
     * Optional (not defaulted) so grants persisted before this capability
     * existed parse unchanged — and, being absent, keep it OFF.
     */
    readLogs: z.boolean().optional(),
    /**
     * May spawn ephemeral DELEGATED worker threads in the target group
     * (delegate_task): each child gets one task, reports back via a correlated
     * completion event, and stops itself. Self-grant (target = the granting
     * agent) = delegate within one's own group. Optional caps override the
     * host defaults (10 live children, depth 2).
     */
    delegateTask: z
        .object({
        maxChildren: z.number().int().min(1).max(50).optional(),
        maxDepth: z.number().int().min(1).max(5).optional(),
    })
        .optional(),
    /**
     * May create and manage long-lived operation workers in the target group.
     * The host enforces the configured cap and a global hard maximum of 50.
     * Operation workers cannot use this grant to create nested teams.
     */
    manageWorkers: z
        .object({
        maxWorkers: z.number().int().min(1).max(50).default(10),
    })
        .optional(),
    /** May post events to the target agent's threads, restricted to these kinds × deliveries. */
    sendEvent: z
        .object({
        kinds: z.array(PersistentAgentPeerEventKindSchema).min(1),
        deliveries: z.array(PersistentAgentEventDeliverySchema).min(1),
    })
        .optional(),
    /** May post one event to EVERY live thread of the target agent at once (fan-out), restricted to these kinds × deliveries. */
    broadcastEvent: z
        .object({
        kinds: z.array(PersistentAgentPeerEventKindSchema).min(1),
        deliveries: z.array(PersistentAgentEventDeliverySchema).min(1),
    })
        .optional(),
    /** May upsert facts on the target agent's threads, restricted to these lanes ('*' = any). */
    putFact: z
        .object({
        lanes: z.array(z.string().min(1).max(200)).min(1).max(100),
    })
        .optional(),
    /**
     * May READ the target group's append-only interaction log
     * (PersistentAgentInteraction, via read_agent_activity) — the structured audit
     * trail of what its threads did: thread lifecycle, events, facts, and worker
     * exceptions. Distinct from `readLogs`: that grants the message *transcript*
     * of one thread (list_peer_messages); this grants the cross-thread *activity*
     * log of a whole group. It is the read path for a "dreaming" reflector PA — a
     * dedicated ORCHESTRATION thread granted readActivity over several fleets can
     * spot org-wide patterns no single supervisor sees, then propose memories via
     * the governed save_memory. Read-only; not a transcript, though a row's
     * `detail` may carry a worker's own (untrusted) exception summary. Optional so
     * grants persisted before this capability existed parse unchanged.
     */
    readActivity: z.boolean().optional(),
    /**
     * May hand one of its own memories to the target agent (DEV-17210,
     * `share_memory`) — the memory becomes bound to the target and resolves for
     * it from then on, with an `AgentMemoryShare` row recording who shared what
     * with whom.
     *
     * A grant of this is narrower than it sounds and wider than it looks:
     *   - narrower, because it can only share memories the sharer can ALREADY
     *     see (its own resolved set) and can only ADD a binding — never edit,
     *     archive, or rescope the memory itself;
     *   - wider, because the target's operator did not review the content. That
     *     is why it is its own capability rather than riding along with
     *     `sendEvent`: telling an agent something and changing what it durably
     *     believes are different powers.
     *
     * `types` restricts which memory types may cross ('*' = any). The common
     * safe grant is `{ types: ['FACT', 'EVENT', 'OBSERVATION'] }` — share what
     * you observed, not what you were told to do. `maxPerHour` bounds the flow
     * so a looping agent can't fill a peer's store; the host defaults it.
     * Optional so grants persisted before this capability existed parse
     * unchanged — and, being absent, keep it OFF.
     */
    shareMemory: z
        .object({
        types: z.array(z.string().min(1).max(40)).min(1).max(20).default(['*']),
        maxPerHour: z.number().int().min(1).max(500).optional(),
    })
        .optional(),
});
/**
 * One directional peer grant as supplied on create/update: "agents of THIS
 * orchestration agent may do `capabilities` to threads of `targetAgentId`".
 * Directional — a reply channel requires a reciprocal grant on the other
 * agent. The target may be the granting agent itself (self-spawn / self-notify
 * patterns).
 */
export const PersistentAgentPeerGrantInputSchema = z.object({
    targetAgentId: z.string().min(1),
    capabilities: PersistentAgentPeerCapabilitiesSchema,
});
/** A persisted peer grant row (join table agentId → targetAgentId). */
export const PersistentAgentPeerGrantSchema = PersistentAgentPeerGrantInputSchema.extend({
    id: z.string(),
    orgId: z.string(),
    /** The granting orchestration agent — whose threads get the peer tools. */
    agentId: z.string(),
    /** Denormalized target agent name for display; null when the target was deleted. */
    targetAgentName: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Attribute key carrying the agent→agent chain depth on peer-sent events. Each
 * peer send copies (hops of the event that woke the sender) + 1; sends beyond
 * persistentAgentPeerMaxHops are refused, killing A→B→A ping-pong loops.
 */
export const persistentAgentHopsAttribute = 'hops';
export const persistentAgentPeerMaxHops = 50;
/** Max threads one broadcast_peer_event call may fan out to. */
export const persistentAgentBroadcastMaxTargets = 50;
/** Default / hard-max messages one list_peer_messages call returns (`readLogs`). */
export const persistentAgentPeerMessagesDefaultLimit = 50;
export const persistentAgentPeerMessagesMaxLimit = 200;
/** Default / hard-max turns one list_peer_turns call returns (`readLogs`). */
export const persistentAgentPeerTurnsDefaultLimit = 20;
export const persistentAgentPeerTurnsMaxLimit = 100;
/**
 * Roles a `persistent_agent_log` row can actually carry. Enumerated from the
 * only writers — `readTranscript` keeps `assistant` | `tool`
 * (packages/mastra-layer/src/transcript.ts), and the host writes a literal
 * `'assistant'` for an in-progress turn and `'user'` for an inbound message
 * (apps/persistent-agent-host/src/session-host.ts). NOTE: the schema's own
 * comment on the column also lists `system`, but no writer emits it.
 */
export const persistentAgentLogRoles = ['assistant', 'tool', 'user'];
/** Excerpt context (chars each side of a search hit) — default / hard max. */
export const persistentAgentPeerSearchContextDefault = 300;
export const persistentAgentPeerSearchContextMax = 2000;
/**
 * Response budget for one `list_peer_messages` call, in characters of rendered
 * message text. A transcript read is the easiest way for an agent to blow its
 * own context window, so the reply is truncated at this budget and the
 * truncation is reported (`truncated: true`) rather than silently applied.
 */
export const persistentAgentPeerMessagesMaxChars = 60_000;
/**
 * Per-payload cap (chars) on each tool-call `args` / `result` a
 * `list_peer_messages` reply carries — default / hard max. Separate from the
 * response budget above because it bounds ONE blob: a single tool result can be
 * a whole page of JSON, and the point of the structured field is to give the
 * reader enough of it to act on, not all of it. Payload characters count
 * against `persistentAgentPeerMessagesMaxChars` like any other text, so this is
 * a shape knob, not a second budget.
 */
export const persistentAgentPeerToolPayloadDefault = 2_000;
export const persistentAgentPeerToolPayloadMaxChars = 20_000;
/**
 * Display info for one agent id, as returned by
 * `GET /v1/persistent-agent-groups/agent-names?ids=…`. Lets the console render
 * `agent:<id>` provenance chips as "agent: <thread name> (<group name>)".
 */
export const PersistentAgentNameInfoSchema = z.object({
    /** The thread's assigned label; null when unnamed (UI falls back to the id). */
    name: z.string().nullable(),
    /** The orchestration agent ("persistent agent" in UI vocabulary) the thread belongs to. */
    agentName: z.string(),
});
export const PersistentAgentNamesResponseSchema = z.record(z.string(), PersistentAgentNameInfoSchema);
/**
 * The PersistentAgentGroup definition row, as persisted. There is no separate
 * "group" table — this IS an `Agent` (`kind: ORCHESTRATION`, `id` shared with
 * this row's backing agent) plus its 1:1 extended config, flattened into one
 * response so a single GET returns everything (no separate "fetch the base
 * agent" round trip). Built on `AgentSchema` so the Agent-level fields can't
 * drift from the single source of truth in `./agent.ts`.
 */
export const PersistentAgentGroupSchema = AgentSchema.extend({
    /** First-class user-facing collection this definition belongs to. */
    orchestrationGroupId: z.string(),
    orchestrationGroup: OrchestrationGroupRefSchema,
    role: PersistentAgentRoleSchema,
    /** Fact lanes every launched agent must supply a value for. */
    requiredFacts: z.array(PersistentAgentRequiredFactSchema),
    /** Optional per-day spend cap (USD) across this agent's threads; null = none. */
    budgetUsdPerDay: z.number().nonnegative().nullable(),
    /** When set, no new threads may start (in-flight threads are unaffected). */
    disabledAt: z.date().nullable(),
    /**
     * Attached filesystem ("Filesystem" in the UI) — threads get the
     * workspace_* file tools for it on the next build (thread start or reset).
     * Optional (not just nullable) so responses from servers predating
     * filesystems parse.
     */
    filesystemId: z.string().nullable().optional(),
    /**
     * "Can create dashboards/widgets" — when true the host injects the
     * dashboard_* toolset on the next build (thread start or reset). Optional
     * (not just defaulted) so
     * responses from servers predating dashboards parse.
     */
    dashboardsAllowed: z.boolean().optional(),
    /**
     * Outbound peer grants — what THIS agent's threads may do to other agents.
     * Optional (not just defaulted) so responses from servers predating peer
     * grants still parse.
     */
    peers: z.array(PersistentAgentPeerGrantSchema).optional(),
    /**
     * Inbound grants — agents whose threads may act on THIS agent's threads.
     * Read-only (managed from the granting agent's side); optional for the same
     * compatibility reason as `peers`.
     */
    grantedBy: z.array(PersistentAgentPeerGrantSchema).optional(),
});
/** The PersistentAgent row — one living, durable instance of a group. */
export const PersistentAgentSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /** The orchestration Agent (PersistentAgentGroup) this thread was spawned from. */
    agentId: z.string(),
    /** Mastra thread id (the durable conversation). Defaults to the agent id. */
    threadId: z.string(),
    /** Mastra resource id — org/agent/thread scoped (see persistentAgentResourceId). */
    resourceId: z.string(),
    /**
     * Optional user-facing label for this thread; null = unnamed (the UI falls
     * back to the id). Set at start or renamed later — never used for addressing.
     * Optional (not just nullable) so responses from servers predating names
     * still parse.
     */
    name: z.string().nullable().optional(),
    /**
     * Attribution: the user principal (JWT subject) who started this thread
     * (DEV-16875). Null/absent = system/fleet-created. NOT an access gate —
     * threads are org-visible multiplayer chats by default; clients use this
     * for "started by me" filters and speaker labels. Optional so responses
     * from servers predating attribution still parse.
     */
    userSubject: z.string().nullable().optional(),
    /**
     * Whether this thread is exposed on the chat + MCP listing surfaces
     * (`orchestration_list_threads` and, transitively, the chat persistent
     * agent). Default false = hidden. Console/operator listings ignore this and
     * keep full visibility — the flag only narrows the chat/MCP surface, which
     * opts in to it. Optional so responses from servers predating the field
     * still parse.
     */
    visibleToChat: z.boolean().optional(),
    status: PersistentAgentStatusSchema,
    /**
     * Self-reported "what I'm working on" line, set by the agent via its
     * `set_status` tool. Free text; distinct from the lifecycle `status` enum.
     */
    statusText: z.string().nullable(),
    /** When the agent last set statusText. */
    statusTextAt: z.date().nullable(),
    /**
     * Self-reported health tint, set by the agent via its `set_status_color`
     * tool. Null = never set (the console falls back to its lifecycle-status
     * colors). Optional so responses from servers predating the field parse.
     */
    statusColor: PersistentAgentStatusColorSchema.nullable().optional(),
    /** Short clause explaining the CURRENT color; null when never set. */
    statusColorReason: z.string().nullable().optional(),
    /** When the agent last shifted its color. */
    statusColorAt: z.date().nullable().optional(),
    /** Host worker that currently owns the loop (Redis-locked); null when unclaimed. */
    ownerWorkerId: z.string().nullable(),
    startedAt: z.date(),
    lastWakeAt: z.date().nullable(),
    lastActivityAt: z.date().nullable(),
    /**
     * Timestamp of the most recent transcript entry (user or agent message) —
     * distinct from `lastActivityAt`, which also bumps on wakes / status changes
     * / timers. Chat thread lists sort + label by this so a thread's recency
     * reflects the conversation, not background activity. Null for threads with
     * no messages yet. Optional so fleet-list producers may omit it.
     */
    lastMessageAt: z.date().nullable().optional(),
    stoppedAt: z.date().nullable(),
    /** How many times an event has woken this agent from idle. */
    wakeCount: z.number().int().nonnegative(),
    /** Cumulative spend (USD) attributed to this agent. */
    totalCostUsd: z.number().nonnegative(),
    /**
     * When another persistent agent started this thread (via its
     * `start_peer_thread` tool), the spawning agent's id; null for user/API
     * starts. Optional so responses from servers predating peer tools parse.
     */
    spawnedByAgentId: z.string().nullable().optional(),
});
// ─── Heartbeat ─────────────────────────────────────────────────────────────
/** Lifecycle of a heartbeat. A paused heartbeat keeps its config but never fires. */
export const PersistentAgentHeartbeatStatusSchema = z.enum(['ACTIVE', 'PAUSED']);
/**
 * A recurring wake schedule attached to an agent (thread) — an agent may have
 * several. On each beat the host fires a normal `wake` event (delivery
 * `when-idle`, source `timer:heartbeat`) carrying `prompt` as its content, so
 * a beat wakes an idle agent and queues on a busy one. `nextFireAt` is the
 * authoritative "when does it fire next" the console shows.
 */
export const PersistentAgentHeartbeatSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    /** Optional user-facing label to tell heartbeats apart; falls back to the cron in the UI. */
    label: z.string().nullable(),
    /** Cron expression (5-part) driving the beat cadence. */
    cron: z.string(),
    /** IANA timezone the cron resolves in; null = UTC. */
    timezone: z.string().nullable(),
    /** Delivered as the wake event's content on every beat. */
    prompt: z.string(),
    status: PersistentAgentHeartbeatStatusSchema,
    /** Next due time — advanced by the host as each beat fires. */
    nextFireAt: z.date(),
    /** When the last beat fired; null until the first fire. */
    lastFiredAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/** A single thread as returned by the detail endpoint. */
export const PersistentAgentDetailSchema = PersistentAgentSchema.extend({
    /** The thread's heartbeats (recurring wake schedules); empty when none are attached. */
    heartbeats: z.array(PersistentAgentHeartbeatSchema),
});
/**
 * An agent as returned by the list endpoint — augmented with `facts`, a map of
 * the group's required lane → current display value, so the list can show the
 * important (required) facts inline per row.
 */
export const PersistentAgentListItemSchema = PersistentAgentSchema.extend({
    facts: z.record(z.string(), z.string()),
    /** The thread's heartbeats (rate + next fire per beat); empty when none are attached. */
    heartbeats: z.array(PersistentAgentHeartbeatSchema),
    /**
     * The agent's self-reported progress bar (its `set_progress` tool),
     * derived from the `progress` fact lane; null once cleared or never set.
     * Optional so responses from servers predating progress bars still parse.
     */
    progressBar: PersistentAgentProgressBarSchema.nullable().optional(),
    /**
     * Lifetime token totals summed from the thread's turns. Optional so list
     * responses from servers predating the column still parse. Zero when the
     * thread has no turns.
     */
    tokensIn: z.number().int().nonnegative().optional(),
    tokensOut: z.number().int().nonnegative().optional(),
});
/**
 * Fleet cost roll-up for the Agents-page cost panel. `byAgent` used to be a
 * separate bucket from `byGroup` (base Agent vs. group could differ when
 * several groups shared one base agent); now that a persistent agent's
 * definition IS its backing Agent, those two buckets are identical, so
 * there's just one.
 */
export const PersistentAgentCostSummarySchema = z.object({
    totalUsd: z.number(),
    byAgent: z.array(z.object({
        agentId: z.string(),
        name: z.string(),
        usd: z.number(),
        sessionCount: z.number().int(),
    })),
    byStatus: z.array(z.object({
        status: z.string(),
        usd: z.number(),
        sessionCount: z.number().int(),
    })),
});
/**
 * A PersistentAgentEvent — the "doorbell". Append-only; carries provenance.
 * `source` is a structured-ish string identifying who/what emitted it:
 *   "user:<accountId>" · "system:<producer>" · "timer:self" · "timer:heartbeat" ·
 *   "agent:<persistentAgentId>" · "webhook:<provider>". The watch UI renders a chip
 *   from it. `wokeFromIdle` marks the events that caused an idle→working edge.
 */
/**
 * A console page the chat agent may navigate the user to (mirrors the
 * theopolis chat contract — the frontend maintains the canonical list and
 * ships it per turn; templates are never resolved server-side).
 */
export const PersistentAgentPageDescriptorSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(2000),
    urlTemplate: z.string().min(1).max(1000),
});
/**
 * Per-turn EXECUTION options riding an event (DEV-16876) — applied by the
 * host to the turn the event wakes/steers, never rendered into message
 * content (that's what `attributes` is for). Additive + ignorable: non-chat
 * callers simply omit it.
 *
 *  - `thinking`: extended-thinking toggle for the woken turn (Anthropic
 *    models; same tri-state semantics as the chat surface — absent = model
 *    default).
 *  - `pageCatalog`: the chat surface's per-turn navigable-pages list.
 *    Accepted + persisted for forward-compat; APPLIED once the PA chat build
 *    carries navigation tools (DEV-16871 parity work).
 */
export const PersistentAgentTurnOptionsSchema = z.object({
    thinking: z.boolean().optional(),
    pageCatalog: z.array(PersistentAgentPageDescriptorSchema).max(200).optional(),
});
export const PersistentAgentEventSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    kind: PersistentAgentEventKindSchema,
    delivery: PersistentAgentEventDeliverySchema,
    content: z.string(),
    /** Provenance string (see convention above). */
    source: z.string(),
    /** Optional XML-safe attributes surfaced to the model (e.g. {pr:"123"}). */
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullable(),
    /** Whether this event woke the agent from idle. */
    wokeFromIdle: z.boolean(),
    /** Per-turn execution options (DEV-16876); null for events that carry none. */
    turnOptions: PersistentAgentTurnOptionsSchema.nullable().optional(),
    createdAt: z.date(),
});
/**
 * A PersistentAgentFact — the "dashboard". The current value of a named lane;
 * exactly the three properties asked for plus the lane key it keys on.
 * Re-asserting the same `data` is a no-op (deduped in the mastra layer).
 */
export const PersistentAgentFactSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    /** Lane key (one current value per lane): "robot", "mission", "tasks", "fleet". */
    laneId: z.string(),
    /** Visual type (drives the dashboard widget). */
    type: PersistentAgentFactTypeSchema,
    /** The fact payload (JSON). */
    data: z.unknown(),
    /** When the fact was asserted/observed. */
    date: z.date(),
    /** Optional human/LLM-facing one-liner shown for the lane. */
    summary: z.string().nullable(),
    updatedAt: z.date(),
});
/**
 * A PersistentAgentLog entry — the persisted "transcript" of what the agent did
 * (a consolidated turn: assistant text or a tool call). Replayed by the watch
 * view on a cold load, since the live chunk stream is ephemeral. One row per
 * Mastra message id (upserted as a turn streams).
 */
export const PersistentAgentLogSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    /** Mastra message id — the upsert key. */
    messageId: z.string(),
    /** assistant | tool | user | system */
    role: z.string(),
    /** The consolidated turn payload (currently `{ text }`). */
    content: z.unknown(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * A readable projection of one `PersistentAgentLog` row — the flattened
 * text a human (or an orchestrating agent) would want when browsing recent
 * messages, rather than the raw chunk JSON in `PersistentAgentLog.content`.
 * Powers `GET .../messages`.
 */
export const PersistentAgentMessageSchema = z.object({
    id: z.string(),
    /** assistant | tool | user | system */
    role: z.string(),
    /** Flattened, human-readable rendering of this turn's chunks (or the legacy `{ text }`). */
    text: z.string(),
    createdAt: z.date(),
});
/**
 * One recorded tool call — a row the host writes as the thread streams
 * (created at tool-call time, completed with duration + error flag at
 * tool-result time). The per-thread projection behind the Tool Usage panel.
 */
export const PersistentAgentToolCallSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    /** Mastra tool-call id — pairs the call with its result. */
    toolCallId: z.string(),
    toolName: z.string(),
    /** The call's arguments (JSON), captured at call time. */
    args: z.unknown(),
    /** True when the tool returned an error. */
    isError: z.boolean(),
    /** Wall-clock call → result. Null while running / result never observed. */
    durationMs: z.number().int().nonnegative().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Aggregate stats for ONE parameter (top-level arg key) of one tool — how often
 * it appears, its numeric range when values are numbers, and its most common
 * values (stringified) otherwise. Powers the "common parameters and ranges"
 * breakdown on the thread detail page.
 */
export const PersistentAgentToolParameterStatsSchema = z.object({
    /** Top-level key in the tool's args object. */
    key: z.string(),
    /** How many recorded calls supplied this parameter. */
    count: z.number().int().nonnegative(),
    /** Range over numeric values of this parameter; null when never numeric. */
    numeric: z.object({ min: z.number(), max: z.number(), avg: z.number() }).nullable(),
    /** Most common values (stringified, truncated), descending by count. */
    topValues: z.array(z.object({ value: z.string(), count: z.number().int().nonnegative() })),
});
/** Aggregate stats for one tool across a thread's recorded calls. */
export const PersistentAgentToolStatsSchema = z.object({
    name: z.string(),
    count: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    /** Over calls with a recorded duration; null when none completed yet. */
    durationMs: z.object({ avg: z.number(), min: z.number(), max: z.number() }).nullable(),
    lastUsedAt: z.date().nullable(),
    /** Per-parameter breakdown, descending by count. */
    parameters: z.array(PersistentAgentToolParameterStatsSchema),
});
/**
 * Tool usage roll-up for one thread — what tools it uses, how long they take,
 * and what parameters they're called with. Served by
 * `GET /v1/persistent-agent-groups/:id/agents/:agentId/tools`.
 */
export const PersistentAgentToolUsageSchema = z.object({
    totalCalls: z.number().int().nonnegative(),
    totalErrors: z.number().int().nonnegative(),
    /**
     * How many most-recent calls the per-tool breakdown was computed over.
     * Equal to totalCalls unless the history exceeded the aggregation window.
     */
    sampledCalls: z.number().int().nonnegative(),
    /** Per-tool stats, descending by call count. */
    tools: z.array(PersistentAgentToolStatsSchema),
});
// ─── Per-turn metrics + trace ─────────────────────────────────────────────
/** Lifecycle of one turn (a `start`→`finish`/`error`/`abort` thread run). */
export const PersistentAgentTurnStatusSchema = z.enum(['running', 'finished', 'error', 'aborted']);
/** One tool call made during a turn — the Trace view's leaf node. */
export const PersistentAgentTurnToolCallSchema = z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    /**
     * The call's arguments, captured at call time. Present on the row all along
     * (`PersistentAgentToolCall.args`) but omitted here until now, which made the
     * Trace view — and `GET .../turns` — show WHICH tools ran and never with what.
     * Diagnosing a misbehaving agent needs the arguments: "launched three drones"
     * and "launched the same drone three times" are the same trace without them.
     */
    args: z.unknown(),
    isError: z.boolean(),
    /** Wall-clock call→result; null while running or if never observed. */
    durationMs: z.number().int().nonnegative().nullable(),
    createdAt: z.date(),
});
/**
 * Per-turn metrics the host captures at finalize time, plus the turn's tool
 * calls (in call order) for the Trace view. Powers the Metrics (time-series)
 * and Trace (turn → its tool calls) tabs.
 */
export const PersistentAgentTurnSchema = z.object({
    id: z.string(),
    status: PersistentAgentTurnStatusSchema,
    startedAt: z.date(),
    finishedAt: z.date().nullable(),
    /** Wall-clock start→finish; null while running. */
    durationMs: z.number().int().nonnegative().nullable(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
    /** Estimated USD; null when the model's pricing is unknown. */
    usd: z.number().nonnegative().nullable(),
    toolCalls: z.number().int().nonnegative(),
    toolErrors: z.number().int().nonnegative(),
    /** Goal-loop objective active when the turn ran, if any. */
    objective: z.string().nullable(),
    /** The turn's tool calls, oldest first. */
    tools: z.array(PersistentAgentTurnToolCallSchema),
});
/** Lifetime roll-up across ALL of a thread's turns (not just the sampled window). */
export const PersistentAgentTurnRollupSchema = z.object({
    totalTurns: z.number().int().nonnegative(),
    totalUsd: z.number().nonnegative(),
    totalTokensIn: z.number().int().nonnegative(),
    totalTokensOut: z.number().int().nonnegative(),
    /** Mean finished-turn duration; null when no turn has finished. */
    avgDurationMs: z.number().nonnegative().nullable(),
    totalToolCalls: z.number().int().nonnegative(),
    totalToolErrors: z.number().int().nonnegative(),
});
/**
 * Per-turn metrics + trace for one thread. Served by
 * `GET /v1/persistent-agent-groups/:id/agents/:agentId/turns`.
 */
export const PersistentAgentTurnsSchema = z.object({
    rollup: PersistentAgentTurnRollupSchema,
    /** Recent turns, newest first (capped at `sampledTurns`). */
    turns: z.array(PersistentAgentTurnSchema),
    /** How many turns the list was capped to. */
    sampledTurns: z.number().int().nonnegative(),
});
// ─── HTTP surface — groups ───────────────────────────────────────────────
/**
 * Creating a persistent agent creates its backing `Agent` (kind:
 * ORCHESTRATION) directly — there's no separate "pick an existing base agent"
 * step anymore, so this extends the same `CreateAgentInputSchema` used by the
 * plain Agent endpoint with the persistent-agent-only fields.
 */
export const CreatePersistentAgentGroupInputSchema = CreateAgentInputSchema.extend({
    /** Existing collection to place this definition in. Omit only for legacy callers (Default). */
    orchestrationGroupId: z.string().optional(),
    /** Create a collection inline and place the definition in it. Mutually exclusive with id. */
    newOrchestrationGroup: CreateOrchestrationGroupInputSchema.optional(),
    /** Single kind for now; defaults to WORKER so callers needn't supply it. */
    role: PersistentAgentRoleSchema.default('WORKER'),
    /** Fact lanes every launched agent must supply a value for. */
    requiredFacts: z.array(PersistentAgentRequiredFactSchema).max(50).default([]),
    budgetUsdPerDay: z.number().nonnegative().optional(),
    /**
     * Peer grants — which other agents this agent's threads may talk to, and
     * how. Full-replace semantics on update (like requiredFacts). At most one
     * grant per target agent.
     */
    peers: z.array(PersistentAgentPeerGrantInputSchema).max(50).default([]),
    /** Filesystem to attach ("Filesystem" in the UI); omit for none. */
    filesystemId: z.string().optional(),
    /** "Can create dashboards/widgets" — inject the dashboard_* toolset. */
    dashboardsAllowed: z.boolean().default(false),
});
/**
 * Duplicate an existing orchestration agent into a fresh, isolated definition —
 * the programmatic path for standing up experiment arms (a variant prompt /
 * model / code-mode) or per-world benchmark copies without hand-editing the UI
 * or mutating the source. Copies every definition field + its orchestration
 * config; provisions a NEW filesystem (never shares the source's); carries peer
 * grants over only when the copy lands in the source's own group (their targets
 * don't exist elsewhere). (DEV-17202)
 */
export const DuplicatePersistentAgentInputSchema = z.object({
    /** Name for the new agent. Must be unique within the target group. */
    name: z.string().min(1).max(200),
    /**
     * Group to place the copy in. Omit to duplicate within the source agent's
     * own group. When set to a different group, peer grants are NOT carried over.
     */
    orchestrationGroupId: z.string().optional(),
});
export const UpdatePersistentAgentGroupInputSchema = CreatePersistentAgentGroupInputSchema.partial().extend({
    /** Move to an existing collection. */
    orchestrationGroupId: z.string().optional(),
    /** Create a collection inline and move this definition into it. */
    newOrchestrationGroup: CreateOrchestrationGroupInputSchema.optional(),
    // null clears the budget; undefined leaves unchanged.
    budgetUsdPerDay: z.number().nonnegative().nullable().optional(),
    /** Pass true to disable (block new agents), false to re-enable. */
    disabled: z.boolean().optional(),
    /** null detaches the filesystem; undefined leaves unchanged. */
    filesystemId: z.string().nullable().optional(),
});
// ─── HTTP surface — agents ───────────────────────────────────────────────
/**
 * One fact lane to seed when an agent starts. An agent has no built-in
 * "mission" — anything it should always see (its purpose, the robot it watches,
 * fleet status…) is just an initial fact, managed exactly like every other fact
 * via the facts API afterwards.
 */
export const PersistentAgentInitialFactSchema = z.object({
    /** Lane key (one current value per lane), e.g. "mission", "robot", "fleet". */
    laneId: z.string().min(1).max(200),
    type: PersistentAgentFactTypeSchema.default('text'),
    /** The fact payload (JSON). */
    data: z.unknown(),
    /** Optional one-liner the model sees; defaults to a JSON rendering of data. */
    summary: z.string().max(2000).optional(),
});
/**
 * Provenance of a thread's `name` — the single naming-authority contract.
 * SEEDED = provisional (truncated first message; whichever surface created or
 * first-messaged the thread) and MAY be upgraded by the host's post-turn LLM
 * auto-namer. GENERATED = the auto-namer's title (run once). USER = a human
 * chose it — never overwritten by any automatic path.
 */
export const PersistentAgentNameSourceSchema = z.enum(['SEEDED', 'GENERATED', 'USER']);
/** Body for `POST /v1/persistent-agent-groups/:id/agents` — start a live agent. */
export const StartPersistentAgentInputSchema = z.object({
    /** Optional user-facing label for the thread; omit to leave it unnamed. */
    name: z.string().min(1).max(200).optional(),
    /**
     * Provenance of `name` (only meaningful when `name` is set). Defaults to
     * USER — a caller-typed label that the auto-namer must never overwrite.
     * Pass SEEDED for a provisional first-message title the auto-namer may
     * upgrade after the first turn.
     */
    nameSource: PersistentAgentNameSourceSchema.exclude(['GENERATED']).optional(),
    /** Fact lanes to pin at start (optional). Seeded as normal facts. */
    initialFacts: z.array(PersistentAgentInitialFactSchema).max(50).default([]),
    /**
     * Benchmark experiment-arm overrides, written to the thread row at creation
     * so the host build reads them on every claim (durable across re-claim —
     * see RunProfileSchema). Service-level contract only: the benchmark module
     * passes this via direct service calls; the public HTTP start endpoint uses
     * PublicStartPersistentAgentInputSchema, which omits it (the zod pipe
     * strips the key if a caller sends it).
     */
    profileOverrides: RunProfileSchema.optional(),
});
/**
 * The public HTTP start-thread body: everything above except
 * `profileOverrides`, which is reserved for the benchmark module's direct
 * service calls so experiment arms are always attributable to a benchmark
 * run (an override-started thread outside a run would be untracked).
 */
export const PublicStartPersistentAgentInputSchema = StartPersistentAgentInputSchema.omit({
    profileOverrides: true,
});
/**
 * Body for `PATCH /v1/persistent-agent-groups/:id/agents/:agentId` — update a
 * thread's mutable surface. All fields are optional (a PATCH may set any
 * combination); omitting a field leaves it unchanged. `name: null` clears the
 * label (the UI falls back to the id). `nameSource` qualifies a `name` write:
 * it defaults to USER (a manual rename, final); pass SEEDED for a provisional
 * auto-title the host's LLM namer may later upgrade — a SEEDED patch never
 * downgrades an existing USER or GENERATED name (server-enforced).
 * `visibleToChat` toggles whether the thread is exposed on the chat + MCP
 * listing surfaces (default hidden).
 */
export const UpdatePersistentAgentInputSchema = z.object({
    name: z.string().min(1).max(200).nullable().optional(),
    nameSource: PersistentAgentNameSourceSchema.exclude(['GENERATED']).optional(),
    visibleToChat: z.boolean().optional(),
});
/**
 * "Started by me" vs "everyone's" filter for the chat sidebar (DEV-16875).
 * `me` narrows a CHAT group's list to threads the authenticated caller started
 * (matched against `PersistentAgent.userSubject`); `all` (the default) applies
 * no owner filter. This is a filter, NOT a privacy gate — every chat stays
 * org-visible and multiplayer. Ignored for ORCHESTRATION (fleet) groups, whose
 * threads have no user owner.
 */
export const PersistentAgentOwnerFilterSchema = z.enum(['me', 'all']);
/** Query string for `GET /v1/persistent-agent-groups/:id/agents`. */
export const PersistentAgentListQuerySchema = z
    .object({
    status: PersistentAgentStatusSchema.optional(),
    owner: PersistentAgentOwnerFilterSchema.optional(),
    /**
     * Opt-in chat/MCP visibility filter. When true, only threads flagged
     * `visibleToChat` are returned — the filter the `orchestration_list_threads`
     * MCP tool (and, transitively, the chat persistent agent) passes so chats
     * see only threads explicitly surfaced to them. Absent/false = unchanged
     * behavior (console/operators keep full visibility). A filter, NOT a
     * privacy gate.
     */
    visibleToChat: z.boolean().optional(),
})
    .strict();
/** Body for `POST /v1/persistent-agent-groups/:id/agents/:agentId/events`. */
export const PostPersistentAgentEventInputSchema = z.object({
    kind: PersistentAgentEventKindSchema.default('instruction'),
    delivery: PersistentAgentEventDeliverySchema.default('now'),
    content: z.string().min(1).max(20_000),
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    turnOptions: PersistentAgentTurnOptionsSchema.optional(),
});
/**
 * Body for `POST /v1/persistent-agent-groups/:id/agents/:agentId/attach-incident`.
 *
 * Scopes a chat thread to a workflow run ("incident") so the agent can reason
 * about it. The endpoint server-side fetches the run STRUCTURE + per-node
 * SUMMARIES (never the full traces — those stay behind the `get_run_structure`
 * / `get_node_detail` / `search_run_content` MCP tools) and posts them as a
 * bounded `inbox` context event: the agent picks it up the next time the user
 * speaks, without a disruptive immediate turn.
 */
export const AttachIncidentToThreadInputSchema = z.object({
    runId: z.string().min(1),
});
/** Body for `PUT /v1/persistent-agent-groups/:id/agents/:agentId/facts/:laneId`. */
export const PutPersistentAgentFactInputSchema = z.object({
    type: PersistentAgentFactTypeSchema.default('text'),
    data: z.unknown(),
    /** Optional one-liner the model sees; defaults to a JSON rendering of data. */
    summary: z.string().max(2000).optional(),
});
/**
 * Body for `POST /v1/persistent-agent-groups/:id/agents/:agentId/heartbeats`
 * — add a new heartbeat to the thread (capped at 20 per agent).
 * `nextFireAt` is computed server-side from the cron.
 */
export const PutPersistentAgentHeartbeatInputSchema = z.object({
    /** Optional label to tell this heartbeat apart from the agent's others. */
    label: z.string().min(1).max(200).nullable().optional(),
    /** Cron expression (5-part), e.g. `0 * * * *` for hourly. Validated server-side. */
    cron: z.string().min(1).max(100),
    /** IANA timezone (e.g. "America/New_York") the cron resolves in. */
    timezone: z.string().min(1).max(100).optional(),
    /** Delivered as the wake event's content on every beat. */
    prompt: z
        .string()
        .min(1)
        .max(20_000)
        .default('Heartbeat: review your facts and objective, and take any pending actions.'),
    status: PersistentAgentHeartbeatStatusSchema.default('ACTIVE'),
});
/**
 * Body for `PATCH .../heartbeats/:heartbeatId` — partial update of one
 * heartbeat. Changing `cron`/`timezone` recomputes `nextFireAt`; resuming
 * (PAUSED → ACTIVE) recomputes it from now rather than firing backlogged beats.
 */
export const UpdatePersistentAgentHeartbeatInputSchema = z.object({
    /** null clears the label (falls back to the cron in the UI); undefined leaves unchanged. */
    label: z.string().min(1).max(200).nullable().optional(),
    cron: z.string().min(1).max(100).optional(),
    /** null clears the timezone (host default); undefined leaves unchanged. */
    timezone: z.string().min(1).max(100).nullable().optional(),
    prompt: z.string().min(1).max(20_000).optional(),
    status: PersistentAgentHeartbeatStatusSchema.optional(),
});
/** Lifecycle of a watch. A paused watch keeps its config but never polls. */
export const PersistentAgentWatchStatusSchema = z.enum(['ACTIVE', 'PAUSED']);
/**
 * A watch's optional response projection (see watch-selector.ts): hash and
 * store only the selected subtree, so noisy sibling fields (timestamps,
 * request ids) never register as changes.
 */
export const PersistentAgentWatchSelectorSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => parseWatchSelector(s) !== null, {
    message: 'Invalid selector — use dot paths, numeric indices, and [*] (e.g. "drones[*].status")',
});
/** How a watch's change notification reaches the agent. */
export const PersistentAgentWatchDeliverySchema = z.enum(['when-idle', 'next-turn', 'inbox']);
/** Watch source transport. Only MCP_POLL today; push transports plug in later. */
export const WatchSourceTypeSchema = z.enum(['MCP_POLL']);
/**
 * Optional pure map JS: sandboxed body receiving `{ output }` (post-selector)
 * and returning the shaped payload (or `undefined` to skip the poll).
 * Trigger is always host-owned (hash-on-change or declarative condition) —
 * map JS does not decide when to fire. Shared by put, update, and preview.
 */
const watchTransformFields = {
    /** Pure map JS body; omit to detect on the selector projection directly. */
    code: z.string().min(1).max(20_000).optional(),
    /** Named constants exposed to the map as top-level vars. */
    bindings: z.record(z.string(), z.unknown()).optional(),
    /** Max ms for the map sandbox per poll. */
    timeoutMs: z.number().int().min(100).max(30_000).optional(),
};
/**
 * Body for `PUT /v1/persistent-agent-groups/:id/agents/:agentId/watches/:name` —
 * attach (or replace) one named watch. Upsert semantics: `[thread, name]` is
 * the key; a thread can carry several watches. Upserting resets the poll
 * cursor (`nextPollAt = now`, `lastHash = null`) so the first poll fires soon.
 */
export const PutPersistentAgentWatchInputSchema = z.object({
    /** MCPConnection row whose tool is polled. Must exist in the org. */
    mcpConnectionId: z.string().min(1),
    /** Un-namespaced MCP tool name to invoke. */
    toolName: z.string().min(1).max(200),
    args: z.record(z.string(), z.unknown()).default({}),
    /** Poll cadence in seconds. */
    intervalSec: z.number().int().min(2).max(3600).default(5),
    /** Fact lane upserted with the full response snapshot on change. */
    laneId: z.string().min(1).max(100),
    wakeDelivery: PersistentAgentWatchDeliverySchema.default('when-idle'),
    /**
     * Situational anchors carried on every fire of this watch (DEV-17326):
     * whitelisted keys merged into the wake event's attributes so anchor
     * stamping and anchored retrieval know WHAT the watch is about (the
     * device it monitors, the site it belongs to, ...).
     */
    anchors: MemoryAnchorSetSchema.optional(),
    /** Optional event content override; default names the watch and lane. */
    wakePrompt: z.string().max(2000).optional(),
    /** Optional response projection — watch only this subtree. */
    selector: PersistentAgentWatchSelectorSchema.optional(),
    /** Declarative gate on the (mapped) projected value. Composes with map `code`. */
    condition: WatchConditionSchema.nullable().optional(),
    ...watchTransformFields,
    /** Seconds until the watch auto-pauses (60s..30d). Omit = no expiry. */
    forSeconds: z
        .number()
        .int()
        .min(60)
        .max(30 * 24 * 3600)
        .optional(),
    /** Operator override for the read-only tool-name guard. */
    force: z.boolean().optional(),
});
/**
 * Body for group-watch routes `.../watches` on a persistent-agent GROUP —
 * same config as a thread watch minus lane targeting (emissions fan out to
 * per-subject lanes `{{subject.type}}_{{subject.id}}` on every live thread
 * of the group via an auto-created routing rule).
 */
export const PutPersistentAgentGroupWatchInputSchema = PutPersistentAgentWatchInputSchema.omit({
    laneId: true,
}).extend({
    /** Lane override only used for hash mode group watches (single-emission). */
    laneId: z.string().min(1).max(100).optional(),
});
/**
 * Body for `PATCH .../watches/:name` — partial update. Resuming (PAUSED →
 * ACTIVE) resets `nextPollAt` to now rather than firing backlogged polls.
 */
export const UpdatePersistentAgentWatchInputSchema = z.object({
    mcpConnectionId: z.string().min(1).optional(),
    toolName: z.string().min(1).max(200).optional(),
    args: z.record(z.string(), z.unknown()).optional(),
    intervalSec: z.number().int().min(2).max(3600).optional(),
    laneId: z.string().min(1).max(100).optional(),
    wakeDelivery: PersistentAgentWatchDeliverySchema.optional(),
    wakePrompt: z.string().max(2000).optional(),
    /** null clears the selector (watch the whole response again). */
    selector: PersistentAgentWatchSelectorSchema.nullable().optional(),
    /** Declarative gate; null clears. Ignored when code is set. */
    condition: WatchConditionSchema.nullable().optional(),
    /** Set to enter transform mode; null clears back to hash mode. */
    code: z.string().min(1).max(20_000).nullable().optional(),
    bindings: z.record(z.string(), z.unknown()).nullable().optional(),
    timeoutMs: z.number().int().min(100).max(30_000).optional(),
    status: PersistentAgentWatchStatusSchema.optional(),
    /** New expiry window from now (60s..30d); null clears the expiry. */
    forSeconds: z
        .number()
        .int()
        .min(60)
        .max(30 * 24 * 3600)
        .nullable()
        .optional(),
    /** Operator override for the read-only tool-name guard (toolName changes). */
    force: z.boolean().optional(),
});
/**
 * A watch row as the API returns it (list responses + upsert echoes). The
 * console's WatchesPanel renders these; `lastPolledAt`/`consecutiveErrors`
 * surface poll health, `lastHash` is internal change-detection state.
 */
export const PersistentAgentWatchSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /** Owning thread; null for group-targeted watches. */
    persistentAgentId: z.string().nullable(),
    /** Owning orchestration group; null for thread-targeted watches. */
    targetAgentId: z.string().nullable(),
    name: z.string(),
    mcpConnectionId: z.string(),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()).nullable(),
    intervalSec: z.number().int(),
    laneId: z.string().nullable(),
    wakeDelivery: PersistentAgentWatchDeliverySchema,
    wakePrompt: z.string().nullable(),
    /**
     * Situational anchors stored on the watch effect (DEV-17326), surfaced so
     * the field is not write-only: read-modify-PUT clients re-send what they
     * read and keep the anchors. Null when the watch has none.
     */
    anchors: MemoryAnchorSetSchema.nullable().optional(),
    condition: WatchConditionSchema.nullable().optional(),
    selector: z.string().nullable(),
    /** Transform mode config (null/empty = hash mode). */
    code: z.string().nullable(),
    bindings: z.record(z.string(), z.unknown()).nullable(),
    timeoutMs: z.number().int(),
    sourceType: WatchSourceTypeSchema,
    status: PersistentAgentWatchStatusSchema,
    /** When the watch auto-pauses; null = no expiry. */
    expiresAt: z.date().nullable(),
    lastHash: z.string().nullable(),
    lastPolledAt: z.date().nullable(),
    nextPollAt: z.date(),
    consecutiveErrors: z.number().int(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Body for `POST .../watches/preview` — invoke the tool NOW (twice, ~1.5s
 * apart) without arming anything, so the wizard can show the response shape,
 * the selector projection, and which fields churn between back-to-back polls.
 */
export const PreviewPersistentAgentWatchInputSchema = z.object({
    mcpConnectionId: z.string().min(1),
    toolName: z.string().min(1).max(200),
    args: z.record(z.string(), z.unknown()).default({}),
    selector: PersistentAgentWatchSelectorSchema.optional(),
    /** Optional gate applied to the mapped/projected value in the dry-run. */
    condition: WatchConditionSchema.nullable().optional(),
    ...watchTransformFields,
});
/** Map dry-run inside a preview: shaped payload + whether detection would wake. */
export const PersistentAgentWatchPreviewTransformSchema = z.object({
    /** Value returned by the map JS (post-selector). Absent when skipped/error. */
    mapped: z.unknown().optional(),
    /** True when hash/condition would fire on this sample (vs lastHash=null). */
    wouldWake: z.boolean().optional(),
    error: z.string().optional(),
});
/** One preview sample: raw output, its selector projection, and the change hash. */
export const PersistentAgentWatchPreviewSampleSchema = z.object({
    output: z.unknown(),
    /** `output` with the request's selector applied (equals `output` when none). */
    projected: z.unknown(),
    /** sha256 over the stable stringify of `projected` — what the tick compares. */
    hash: z.string(),
});
/**
 * Preview result. `ok: false` carries the invoke error inline (transport
 * down, unknown tool, oversized response) instead of throwing, so the wizard
 * renders it next to the form — same stance as the MCP test-connection probe.
 */
export const PreviewPersistentAgentWatchResultSchema = z.object({
    ok: z.boolean(),
    error: z.string().optional(),
    samples: z.array(PersistentAgentWatchPreviewSampleSchema),
    /**
     * Selector-grammar-shaped paths that differed between the two samples —
     * fields that churn with no real state change. The wizard highlights these
     * and steers the selector away from them.
     */
    churnedPaths: z.array(z.string()),
    /** Present when the preview request included `code` — dry-run against sample one. */
    transform: PersistentAgentWatchPreviewTransformSchema.optional(),
});
/**
 * Body for `POST .../watches/generate-transform` — LLM-compile a natural-
 * language reshape rule into sandboxed JS that returns the mapped payload
 * (or undefined to skip). `sampleOutput` (from a live preview) is the strongest signal; without it
 * the model still emits a valid skeleton the operator can edit.
 */
export const GenerateWatchTransformInputSchema = z.object({
    /** What should wake the agent, in plain English. */
    prompt: z.string().min(1).max(4_000),
    /** Live (or projected) tool output the transform will see as `input.output`. */
    sampleOutput: z.unknown().optional(),
    /** Selector already applied before the transform — for prompt context only. */
    selector: PersistentAgentWatchSelectorSchema.optional(),
    /** Optional bindings object the transform will receive as top-level consts. */
    bindings: z.record(z.string(), z.unknown()).optional(),
});
export const GenerateWatchTransformResultSchema = z.object({
    code: z.string().min(1).max(20_000),
    /** Short operator-facing note about assumptions the model made. */
    rationale: z.string().max(1_000).nullish(),
});
/**
 * Body for `POST .../watches/generate-setup` — LLM fills the watch wizard from
 * a plain-English ask + MCP tool catalog (+ optional live sample).
 */
export const GenerateWatchSetupInputSchema = z.object({
    mcpConnectionId: z.string().min(1),
    /** What the operator wants, e.g. "wake when any drone battery drops below 30". */
    prompt: z.string().min(4).max(4_000),
    /** Optional live tool sample (from preview) so field names match reality. */
    sampleOutput: z.unknown().optional(),
    /** Optional hint: tool already chosen in the UI. */
    toolName: z.string().min(1).max(200).optional(),
});
export const GenerateWatchSetupResultSchema = z.object({
    mode: z.enum(['value', 'query', 'map']),
    name: z.string().min(1).max(64),
    toolName: z.string().min(1).max(200),
    /** Args object for the tool each poll. */
    args: z.record(z.string(), z.unknown()).default({}),
    selector: z.string().max(500).nullable().optional(),
    condition: z
        .object({
        op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'matches']),
        value: z.union([z.number(), z.string(), z.boolean()]),
    })
        .nullable()
        .optional(),
    /** false = every change while gate holds; true = first cross only. Default false for value gates. */
    conditionRising: z.boolean().optional(),
    query: WatchQuerySchema.nullable().optional(),
    /** Only for mode=map — pure reshape JS. Prefer query/value. */
    code: z.string().max(20_000).nullable().optional(),
    wakeDelivery: PersistentAgentWatchDeliverySchema.optional(),
    wakePrompt: z.string().max(2_000).nullable().optional(),
    intervalSec: z.number().int().min(2).max(3600).optional(),
    forSeconds: z
        .number()
        .int()
        .min(60)
        .max(30 * 24 * 3600)
        .nullable()
        .optional(),
    laneId: z.string().min(1).max(100).optional(),
    rationale: z.string().max(1_000).nullable().optional(),
});
/** One ordered criterion on a set-objective request. Server fills `id` if omitted. */
export const MissionCriterionInputSchema = z.object({
    id: z.string().min(1).max(80).optional(),
    content: z.string().min(1).max(2_000),
    required: z.boolean().default(true),
});
/** Status of one host-owned mission criterion. */
export const MissionCriterionStatusSchema = z.enum(['pending', 'active', 'completed', 'skipped']);
/** Persisted `mission_criteria` fact-lane payload (host-owned checklist). */
export const MissionCriteriaFactSchema = z.object({
    objective: z.string(),
    items: z.array(z.object({
        id: z.string(),
        content: z.string(),
        required: z.boolean(),
        status: MissionCriterionStatusSchema,
        /** 0-based presentation order; it does not impose an execution dependency. */
        order: z.number().int().nonnegative(),
        evidence: z.string().optional(),
        completedAt: z.string().optional(), // ISO
    })),
    done: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    /** Display hint for an open criterion; null when all required criteria complete. */
    activeId: z.string().nullable(),
});
/** Body for `PUT /v1/persistent-agent-groups/:id/agents/:agentId/objective`. */
export const SetPersistentAgentObjectiveInputSchema = z.object({
    /** The durable, thread-scoped objective the agent works toward until a judge says it's done or maxRuns is hit. */
    objective: z.string().min(1).max(20_000),
    /** Override the agent's default goal budget for this objective (iterations). */
    maxRuns: z.number().int().min(1).max(200).optional(),
    /** Judge rubric — the instruction the goal judge uses to decide when the objective is "done". Distinct from the objective (what the agent does). */
    prompt: z.string().max(20_000).optional(),
    /** Override the judge model for THIS objective (a ModelConfig id). Takes precedence over the base agent's config.goal.judgeModelConfigId. */
    judgeModelConfigId: z.string().min(1).max(200).optional(),
    /** Optional reviewable plan criteria. Their list order is presentation-only. */
    criteria: z.array(MissionCriterionInputSchema).max(30).optional(),
    /** When true and criteria omitted/empty, API generates a plan before starting. Default false. */
    autoDecompose: z.boolean().default(false),
});
/** Body for `POST .../objective/decompose` — plan criteria without setting the objective. */
export const DecomposePersistentAgentObjectiveInputSchema = z.object({
    objective: z.string().min(1).max(20_000),
});
/**
 * Body for `POST .../plan` — todo-native plan approval: seed the agent's task
 * list with the steps and wake it. No goal loop, judge, or criteria.
 */
export const ApprovePersistentAgentPlanInputSchema = z.object({
    objective: z.string().min(1).max(20_000),
    steps: z
        .array(z.object({ content: z.string().min(1).max(2_000) }))
        .min(1)
        .max(20),
});
/**
 * Body for `POST .../dream` — consolidate this thread's context (facts, recent
 * events, transcript, mission history) into a durable markdown "dream" file in
 * the group's attached filesystem. `focus` optionally steers the distillation.
 */
export const DreamPersistentAgentInputSchema = z.object({
    focus: z.string().max(2_000).optional(),
});
/** Result of `POST .../dream` — where the dream file landed. */
export const PersistentAgentDreamResultSchema = z.object({
    /** The filesystem the dream was written into (the group's attachment). */
    filesystemId: z.string(),
    filesystemName: z.string(),
    /** Path of the written file inside the filesystem, e.g. `dreams/2026-07-10-0405-ab12cd34.md`. */
    path: z.string(),
    bytes: z.number().int().nonnegative(),
    /** True when no filesystem was attached and one was created + attached for this dream. */
    filesystemCreated: z.boolean(),
    /** First ~200 chars of the dream body — console toast/preview. */
    preview: z.string(),
});
// ─── HTTP surface — external routing + ingest ──────────────────────────────
const PathSchema = z.string().min(1).max(500);
export const PersistentAgentRouteMatchSchema = z.object({
    source: z.string().min(1).max(200).optional(),
    eventTypes: z.array(z.string().min(1).max(200)).max(100).default(['*']),
    subjectType: z.string().min(1).max(100).optional(),
    subjectId: z.string().min(1).max(200).default('*'),
});
export const PersistentAgentRouteTargetSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('agents'),
        agentIds: z.array(z.string().min(1)).min(1).max(100),
    }),
    z.object({
        type: z.literal('agent'),
        agentId: z.string().min(1),
    }),
    z.object({
        type: z.literal('agentFactMatch'),
        agentId: z.string().min(1),
        factLaneId: z.string().min(1).max(200),
        factPath: PathSchema,
        inputPath: PathSchema,
    }),
]);
export const PersistentAgentRouteFactActionSchema = z.object({
    laneTemplate: z.string().min(1).max(500),
    summaryTemplate: z.string().min(1).max(2000).default('{{summary}}'),
    dataPath: PathSchema.default('data'),
});
export const PersistentAgentRouteEventActionSchema = z.object({
    kind: PersistentAgentEventKindSchema.default('notification'),
    delivery: PersistentAgentEventDeliverySchema.default('now'),
    contentTemplate: z.string().min(1).max(20_000).default('{{summary}}'),
});
export const PersistentAgentRouteActionsSchema = z
    .object({
    fact: PersistentAgentRouteFactActionSchema.optional(),
    event: PersistentAgentRouteEventActionSchema.optional(),
})
    .refine((v) => v.fact || v.event, { message: 'At least one action is required' });
export const CreatePersistentAgentRouteInputSchema = z.object({
    name: z.string().min(1).max(200),
    enabled: z.boolean().default(true),
    match: PersistentAgentRouteMatchSchema,
    target: PersistentAgentRouteTargetSchema,
    actions: PersistentAgentRouteActionsSchema,
});
export const UpdatePersistentAgentRouteInputSchema = CreatePersistentAgentRouteInputSchema.partial();
export const PersistentAgentIngestInputSchema = z.object({
    source: z.string().min(1).max(200),
    eventId: z.string().min(1).max(300),
    eventType: z.string().min(1).max(200),
    subject: z.object({
        type: z.string().min(1).max(100),
        id: z.string().min(1).max(200),
    }),
    observedAt: z.string().datetime().optional(),
    summary: z.string().min(1).max(20_000),
    data: z.unknown().optional(),
});
export const PreviewPersistentAgentRouteInputSchema = PersistentAgentIngestInputSchema;
// ─── Transcript chunk (normalized agent activity) ────────────────────────
/**
 * One unit of transcript activity, normalized into a stable, self-describing
 * shape. The browser renders these directly instead of reverse-engineering raw
 * Mastra parts:
 *
 *  - `user`        — an input turn: a delivered event / message that prompted
 *                    the agent (carries provenance when known)
 *  - `text`        — a delta of the assistant's spoken answer
 *  - `reasoning`   — a delta of its thinking / scratchpad trace
 *  - `tool-call`   — it invoked a tool (with the arguments)
 *  - `tool-result` — that tool returned (paired back by `toolCallId`)
 *  - `error`       — the run surfaced an error
 *
 * Agent text/reasoning arrive as deltas on the LIVE stream (so the pane streams
 * live); the console coalesces consecutive deltas and pairs calls with results
 * for display. On cold load the API replays the persisted, already-consolidated
 * turns as these same chunks. Structural markers (start/step/finish/abort/usage)
 * are NOT chunks — the host drops them, so they never reach this contract.
 */
export const PersistentAgentTranscriptChunkSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('user'),
        text: z.string(),
        /** Provenance source (e.g. `user:alice`, `system:telemetry`) when known. */
        source: z.string().optional(),
        /** The delivered event's kind, when this turn came from an event. */
        eventKind: PersistentAgentEventKindSchema.optional(),
    }),
    z.object({ type: z.literal('text'), text: z.string() }),
    z.object({ type: z.literal('reasoning'), text: z.string() }),
    z.object({
        type: z.literal('tool-call'),
        toolCallId: z.string(),
        name: z.string(),
        args: z.unknown(),
        /** Display name of the MCP connection this tool came from, when known. */
        mcpName: z.string().optional(),
    }),
    z.object({
        type: z.literal('tool-result'),
        toolCallId: z.string(),
        /** May be absent — a result can arrive without the host knowing the name. */
        name: z.string().optional(),
        result: z.unknown(),
        durationMs: z.number().optional(),
        /** True when this result is a tool FAILURE — a Mastra tool-error part, or
         *  an MCP `CallToolResult` whose `isError` flag is set — rather than a
         *  successful return. `result` then carries the error body (serialized
         *  error or the error envelope), not a tool payload. Surfaces must not
         *  parse `result` as domain data. */
        isError: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('goal'),
        /** The objective being evaluated (may be omitted by some chunk shapes). */
        objective: z.string().optional(),
        /** Judge verdict: true when the objective is satisfied. */
        passed: z.boolean().optional(),
        /** `active` while iterating, `done` once the judge passes it, or `exhausted` when maxRuns is hit. */
        status: z.string().optional(),
        /** Why the judge scored it this way. */
        reason: z.string().optional(),
        iteration: z.number().int().nonnegative().optional(),
        maxRuns: z.number().int().nonnegative().optional(),
    }),
    z.object({
        type: z.literal('error'),
        message: z.string(),
        /**
         * Classified provider failure kind when known (billing / auth / rate_limit / …).
         * Optional so legacy transcript rows and non-provider errors stay valid.
         */
        kind: ProviderFailureKindSchema.optional(),
        statusCode: z.number().int().optional(),
        retryable: z.boolean().optional(),
    }),
]);
// ─── Live watch frame (host → console SSE contract) ──────────────────────
/**
 * One frame on a session's live stream. The host worker classifies what Mastra
 * emits into these; the console keeps a `Map<laneId,fact>` for `fact` frames
 * (the dashboard) and an append list for `event` frames (the doorbell feed),
 * drives the header off `status`, and renders `chunk` into the transcript.
 */
export const PersistentAgentWatchFrameSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('fact'),
        laneId: z.string(),
        factType: PersistentAgentFactTypeSchema,
        summary: z.string(),
        data: z.unknown(),
        date: z.string(),
    }),
    z.object({
        kind: z.literal('event'),
        eventKind: PersistentAgentEventKindSchema,
        content: z.string(),
        source: z.string(),
        wokeFromIdle: z.boolean(),
        /**
         * True only when this event's `interrupt` delivery actually aborted an
         * in-flight run (idle is a no-op → omitted). Lets the console mark the
         * message as having steered mid-run. Runtime-only: replayed history omits it.
         */
        interrupted: z.boolean().optional(),
        /**
         * Workflow run id when this event ATTACHED an incident to the thread
         * (`attributes.source === 'incident'`). Surfaced so the console can
         * reconstruct the inline incident cards from the durable event feed on
         * every (re)connect — persistence + multiplayer consistency with no
         * separate store. Absent on all other events.
         */
        runId: z.string().optional(),
        at: z.string(),
    }),
    z.object({
        kind: z.literal('status'),
        status: PersistentAgentStatusSchema,
        at: z.string(),
    }),
    z.object({
        /** The agent's self-reported activity line (its `set_status` tool). */
        kind: z.literal('activity'),
        text: z.string(),
        at: z.string(),
    }),
    z.object({
        /**
         * The agent's self-reported health tint changed (`set_status_color`, or
         * `clear_status_color` → `color: null`). Carries the reason so the watch
         * page can caption the shift without a round-trip to the color log.
         */
        kind: z.literal('status_color'),
        color: PersistentAgentStatusColorSchema.nullable(),
        reason: z.string(),
        at: z.string(),
    }),
    z.object({
        kind: z.literal('chunk'),
        /** A normalized transcript chunk — assistant text, reasoning, or tool activity. */
        part: PersistentAgentTranscriptChunkSchema,
    }),
    z.object({
        /**
         * A live per-turn snapshot: `running` the instant a turn starts, then the
         * finalized metrics + tool calls on finish. Upserted by `turn.id` on the
         * client so the Metrics/Trace views update in real time (no poll lag).
         */
        kind: z.literal('turn'),
        turn: PersistentAgentTurnSchema,
        at: z.string(),
    }),
    z.object({
        /**
         * No-op keepalive frame emitted every 25 s by the server to prevent load
         * balancer and proxy idle-connection timeouts. Clients must silently ignore
         * this — it carries no application state.
         */
        kind: z.literal('heartbeat'),
    }),
]);
// ─── Interaction log (actor → agent edges) ────────────────────────────────
/**
 * Who performed an interaction. `agent` = another persistent agent (peer
 * tools); `user` = a human via the API. Designed to extend (`system` for
 * route/ingest deliveries, etc.) without a schema reshape — values are
 * validated here, not as a DB enum, so additions are a code change only.
 */
export const PersistentAgentInteractionActorTypeSchema = z.enum(['agent', 'user']);
/**
 * What happened, at edge granularity. Deliberately high-level: one row means
 * "this actor did this to this thread at this time" — the content behind it
 * stays in the event/fact tables. `denied` records a peer action refused at
 * execution time (missing grant, hop limit) — agent-only, since a user's
 * refusal is an HTTP error they see immediately. `objective_*` are user-only
 * today (agents cannot set objectives). `thread_deleted` (user-only — deletion
 * stays human-only) removes the thread's rows entirely; the graph drops the
 * node, where `thread_stopped` merely ghosts it. `thread_reset` (user-only)
 * also removes the thread's rows entirely, but the node survives: the thread
 * id is preserved and immediately reinstantiated from its base agent's current
 * definition. It is the one interaction whose subject outlives its own
 * history, which is why the log — deliberately FK-free — is not wiped with the
 * rest. `group_created`/
 * `group_deleted` (user-only, `toAgentId` null) keep the graph's cluster set
 * live — deleting a group drops its (empty) cluster without a reconnect.
 */
export const PersistentAgentInteractionTypeSchema = z.enum([
    'group_created',
    'group_deleted',
    'thread_started',
    'thread_stopped',
    'thread_deleted',
    'thread_reset',
    'event_sent',
    'fact_put',
    'objective_set',
    'objective_cleared',
    'objective_interrupted',
    'objective_resumed',
    'dream_written',
    'denied',
    'task_delegated',
    'delegation_completed',
    'plan_seeded',
    'operation_started',
    'operation_paused',
    'operation_resumed',
    'operation_stopped',
    'worker_spawned',
    'worker_retired',
    'worker_exception',
    'scorecard_configured',
]);
/**
 * Tiny, capped tooltip payload — the ONLY detail an interaction carries.
 * Everything here is optional; which fields are set depends on `type`
 * (kind/delivery for event_sent, laneId for fact_put, attempted/reason for
 * denied). Never grow this into a content mirror.
 */
export const PersistentAgentInteractionDetailSchema = z.object({
    kind: PersistentAgentEventKindSchema.optional(),
    delivery: PersistentAgentEventDeliverySchema.optional(),
    /** true when this event_sent row is one leg of a broadcast fan-out. */
    broadcast: z.boolean().optional(),
    laneId: z.string().max(200).optional(),
    /** For `group_created`: the new group's name (the row predates any thread
     *  that could carry it, and the graph needs a cluster label immediately). */
    name: z.string().max(200).optional(),
    /** For `group_created`: the user-facing collection containing the definition. */
    orchestrationGroupId: z.string().optional(),
    /** For `denied`: the peer action that was refused (e.g. "sendEvent"). */
    attempted: z.string().max(100).optional(),
    /** For `denied`: why (e.g. "hop-limit", "no-grant"). Short slug, not prose. */
    reason: z.string().max(200).optional(),
    operationId: z.string().optional(),
    workerId: z.string().optional(),
    revision: z.number().int().nonnegative().optional(),
    /** For `task_delegated`/`delegation_completed`: correlates the parent and
     *  child rows for a single delegation. */
    delegationId: z.string().max(200).optional(),
    /** For `delegation_completed`: the child's reported outcome. */
    status: z.string().max(50).optional(),
});
/**
 * One persisted actor→agent interaction — an append-only edge with timing.
 * The from* columns are set only when the actor is an agent (they keep the
 * graph joinable per side); `toAgentId` is null only for a denied startThread
 * (no thread came to exist). No FKs by design: the log outlives its
 * participants.
 */
export const PersistentAgentInteractionSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    type: PersistentAgentInteractionTypeSchema,
    actorType: PersistentAgentInteractionActorTypeSchema,
    /** PersistentAgent id when actorType='agent'; auth subject when 'user'. */
    actorId: z.string(),
    /** Display name frozen at write time — the log records who it WAS. */
    actorLabel: z.string().nullable(),
    // NOTE: in this schema "*AgentId" names the live PersistentAgent (thread)
    // row, while "*GroupId" names the definition it belongs to (an
    // ORCHESTRATION-kind Agent, now == PersistentAgentGroup.id — see the file
    // header). Kept as `GroupId` rather than renamed to `AgentId` specifically
    // to avoid colliding with the thread-level names below.
    /** Set only when actorType='agent'. */
    fromAgentId: z.string().nullable(),
    fromGroupId: z.string().nullable(),
    toAgentId: z.string().nullable(),
    toGroupId: z.string(),
    /** Agent-to-agent chain depth of the send; 0 for user actions. */
    hops: z.number().int().nonnegative(),
    detail: PersistentAgentInteractionDetailSchema.nullable(),
    createdAt: z.date(),
});
/**
 * Query string for `GET /v1/persistent-agents/interactions`. `agentId` and
 * `groupId` match EITHER side of the edge (actor or target). Cursor pagination
 * newest-first: pass the previous page's `nextCursor`.
 */
export const PersistentAgentInteractionListQuerySchema = z
    .object({
    type: PersistentAgentInteractionTypeSchema.optional(),
    actorType: PersistentAgentInteractionActorTypeSchema.optional(),
    actorId: z.string().optional(),
    agentId: z.string().optional(),
    groupId: z.string().optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
})
    .strict();
/** One page of interactions, newest first. */
export const PersistentAgentInteractionsPageSchema = z.object({
    items: z.array(PersistentAgentInteractionSchema),
    /** Pass back as `cursor` for the next (older) page; null when exhausted. */
    nextCursor: z.string().nullable(),
});
// ─── Interaction stream (snapshot-then-pulses SSE contract) ───────────────
/** One agent node in the graph snapshot (a thread, in UI vocabulary). */
export const PersistentAgentInteractionGraphAgentSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    groupId: z.string(),
    groupName: z.string(),
    status: PersistentAgentStatusSchema,
    /** Latest self-reported `set_status` activity line. */
    statusText: z.string().nullable().optional(),
    /**
     * The thread's self-reported health tint (its `set_status_color` tool) —
     * the graph paints the node ring AND its progress pie slices with it, so a
     * wall monitor reads health at a glance. Null/absent = never set; the graph
     * falls back to its lifecycle-status colors.
     */
    statusColor: PersistentAgentStatusColorSchema.nullable().optional(),
    /** Short clause explaining the current color — shown on node hover. */
    statusColorReason: z.string().nullable().optional(),
    /**
     * The thread's self-reported progress bar (its `set_progress` tool), so
     * the graph can render it as pie slices on the thread's node. Optional so
     * older-server responses (and locally-synthesized `thread_started` nodes)
     * still parse.
     */
    progressBar: PersistentAgentProgressBarSchema.nullable().optional(),
    /** Durable Mastra task-list counts, projected after each turn. */
    taskCount: z.number().int().nonnegative().optional(),
    openTaskCount: z.number().int().nonnegative().optional(),
    taskItems: z
        .array(z.object({
        content: z.string(),
        status: z.enum(['pending', 'in_progress', 'completed']),
    }))
        .optional(),
});
/** One Agent (group) in the graph snapshot — present even with zero threads. */
export const PersistentAgentInteractionGraphGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** User-facing orchestration collection containing this agent definition. */
    orchestrationGroupId: z.string().optional(),
});
/** One user actor seen in the snapshot window. */
export const PersistentAgentInteractionGraphUserSchema = z.object({
    actorId: z.string(),
    actorLabel: z.string().nullable(),
    lastSeenAt: z.string(),
});
/** Aggregated edge over the snapshot window: actor → thread, per type. */
export const PersistentAgentInteractionGraphEdgeSchema = z.object({
    actorType: PersistentAgentInteractionActorTypeSchema,
    actorId: z.string(),
    toAgentId: z.string().nullable(),
    toGroupId: z.string(),
    type: PersistentAgentInteractionTypeSchema,
    count: z.number().int().positive(),
    lastAt: z.string(),
});
/** Wire cap on a memory node's `content` — the graph shows a glyph + tooltip,
 *  not the full text, and a 4,000-char body per memory would bloat the frame. */
export const graphMemoryContentMax = 240;
/**
 * One curated-memory node in the graph. Memory is bound to an
 * AGENT DEFINITION, not to a thread, so these hang off the graph's cluster
 * circles (`agentIds` are group ids) — or, when `appliesToAll`, off the
 * canvas-wide org band rather than any one cluster.
 *
 * Deliberately a projection, not `AgentMemory`: `content` is truncated to
 * `graphMemoryContentMax` and the governance/lineage columns (sourceRunId,
 * evidenceRunId, subjectRef, version chain) are omitted — the graph renders
 * status, type and priority, and links out to /agents/memories for the rest.
 */
export const PersistentAgentInteractionGraphMemorySchema = z.object({
    id: z.string(),
    /** Truncated to `graphMemoryContentMax`; `truncated` marks the elision. */
    content: z.string(),
    truncated: z.boolean().optional(),
    type: MemoryTypeSchema,
    status: MemoryStatusSchema,
    priority: MemoryPrioritySchema,
    /** Org-wide: renders on the top band, not on a cluster arc. */
    appliesToAll: z.boolean(),
    /** Bound agent-definition (group) ids — empty for a purely org-wide memory. */
    agentIds: z.array(z.string()),
    /** ISO `updatedAt` — drives newest-first ordering along the arc. */
    at: z.string(),
});
/** Which lifecycle moment produced a `memory` frame. */
export const PersistentAgentGraphMemoryActionSchema = z.enum([
    'created',
    'approved',
    'rejected',
    'updated',
    'archived',
]);
/**
 * One frame on `GET /v1/persistent-agents/interactions/stream`. On connect the
 * server sends ONE `graph` frame (the current picture: threads — including
 * stopped/failed ones, which the console renders as ghosts in their group —
 * recently seen users, edge aggregates over `windowMinutes`), then
 * `interaction` frames as pulses. A reconnecting client just re-reads the snapshot — no cursor
 * gap-fill. Timestamps are ISO strings (SSE is JSON).
 */
/**
 * The whole picture at one instant — the body of a `graph` frame, named on its
 * own because two surfaces produce it: the live stream (as "the picture now")
 * and the Analyze timeline (as "the picture at `since`", the state playback
 * starts from). Keeping one schema keeps both honest to the same shape.
 */
export const PersistentAgentInteractionGraphSnapshotSchema = z.object({
    windowMinutes: z.number().int().positive(),
    /** Every live Agent (group), threads or not — the graph renders a cluster
     *  circle per group. Optional so older-server frames still parse. */
    groups: z.array(PersistentAgentInteractionGraphGroupSchema).optional(),
    agents: z.array(PersistentAgentInteractionGraphAgentSchema),
    users: z.array(PersistentAgentInteractionGraphUserSchema),
    edges: z.array(PersistentAgentInteractionGraphEdgeSchema),
    /** Live curated memory (not archived/superseded) reachable by the visible
     *  agent definitions, newest-first. Optional so older-server frames and
     *  narrow test fixtures still parse. */
    memories: z.array(PersistentAgentInteractionGraphMemorySchema).optional(),
    at: z.string(),
});
export const PersistentAgentInteractionStreamFrameSchema = z.discriminatedUnion('kind', [
    PersistentAgentInteractionGraphSnapshotSchema.extend({
        kind: z.literal('graph'),
    }),
    z.object({
        kind: z.literal('interaction'),
        interaction: PersistentAgentInteractionSchema.extend({ createdAt: z.string() }),
        /** >1 when identical edges inside the coalescing window were merged. */
        count: z.number().int().positive().optional(),
    }),
    /**
     * A thread's `progress` fact lane changed (`set_progress`/`clear_progress`).
     * Self-reported progress has no actor→target edge to ride (the agent is
     * acting on itself, not a peer) — a plain frame lets the graph update the
     * thread node's pie slices live instead of only picking up the change on
     * the next reconnect's `graph` snapshot.
     */
    z.object({
        kind: z.literal('progress'),
        agentId: z.string(),
        progressBar: PersistentAgentProgressBarSchema.nullable(),
    }),
    /** A thread's durable Mastra task list changed. */
    z.object({
        kind: z.literal('tasks'),
        agentId: z.string(),
        taskCount: z.number().int().nonnegative(),
        openTaskCount: z.number().int().nonnegative(),
        taskItems: z.array(z.object({
            content: z.string(),
            status: z.enum(['pending', 'in_progress', 'completed']),
        })),
    }),
    /** A thread changed its self-reported `set_status` activity line. */
    z.object({
        kind: z.literal('activity'),
        agentId: z.string(),
        statusText: z.string(),
    }),
    /**
     * A thread shifted its self-reported health tint (`set_status_color`, or
     * `clear_status_color` → `statusColor: null`). Like `progress` this is the
     * agent acting on itself, so there is no actor→target edge to ride — a plain
     * frame lets the graph re-tint the node and its pie slices live instead of
     * waiting for the next snapshot.
     */
    z.object({
        kind: z.literal('status_color'),
        agentId: z.string(),
        statusColor: PersistentAgentStatusColorSchema.nullable(),
        statusColorReason: z.string(),
    }),
    /** A thread's runtime lifecycle state changed (for example IDLE → WORKING). */
    z.object({
        kind: z.literal('status'),
        agentId: z.string(),
        status: PersistentAgentStatusSchema,
    }),
    /**
     * A group's peer grants were set/changed/revoked (via create or update).
     * No actor→target edge to ride — this is a config change, not a thread
     * interaction — so a plain frame lets an open Interaction Graph refetch
     * the permission edges live instead of only picking them up on the next
     * reconnect's snapshot (or a manual page refresh).
     */
    z.object({
        kind: z.literal('peer_grants'),
        groupId: z.string(),
        at: z.string(),
    }),
    /**
     * A curated memory landed or changed governance state. Like
     * `peer_grants` this is a definition-level change with no actor→thread edge
     * to ride, so it gets a plain frame: the graph animates the memory onto the
     * bound cluster's arc (or the org band) the moment it appears, instead of
     * only showing it after the next reconnect's snapshot.
     *
     * `archived` and `rejected` are removals — the consumer drops the node.
     * Published by BOTH writers of the shared memory-write path: apps/api (the
     * console + MCP route) and the persistent-agent host (an agent's own
     * `save_memory`).
     */
    z.object({
        kind: z.literal('memory'),
        action: PersistentAgentGraphMemoryActionSchema,
        memory: PersistentAgentInteractionGraphMemorySchema,
    }),
]);
// ─── Analyze timeline (historical playback of the interaction graph) ──────
/** Hard ceiling on frames one timeline response may carry. A denser window is
 *  truncated (see `truncated` / `effectiveUntil`) rather than silently thinned,
 *  so the console can say "the range is denser than this" out loud. */
export const persistentAgentTimelineMaxFrames = 20_000;
export const persistentAgentTimelineDefaultFrames = 5_000;
/**
 * Query string for `GET /v1/persistent-agents/interactions/timeline`. Unlike
 * the live stream this is a CLOSED range: both bounds are required, because the
 * response's meaning ("everything that happened between these instants") has no
 * sensible open-ended reading. `since`/`until` follow the house convention.
 */
export const PersistentAgentTimelineQuerySchema = z
    .object({
    since: z.string().datetime(),
    until: z.string().datetime(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(persistentAgentTimelineMaxFrames)
        .default(persistentAgentTimelineDefaultFrames),
})
    .strict();
/**
 * One moment on the timeline: an explicit timestamp plus the frame to apply.
 *
 * The `at` is carried OUTSIDE the frame on purpose. Live frames only need a
 * timestamp when something renders it (`graph`, `memory`, `peer_grants` have
 * one; `progress`, `tasks`, `activity`, `status`, `status_color` have none —
 * "now" was implicit in their arrival). Replay has no implicit now, so the
 * wrapper supplies it uniformly instead of reshaping five frame types.
 */
export const PersistentAgentTimelineFrameSchema = z.object({
    at: z.string().datetime(),
    frame: PersistentAgentInteractionStreamFrameSchema,
});
/**
 * A replayable window of org history: the picture as it stood at `since`, then
 * every high-level action through `until` in chronological order. The console
 * feeds `frames` through the SAME reducer the live stream drives, on a virtual
 * clock — so playback and live rendering can never diverge.
 *
 * Deliberately edge-granular, matching `PersistentAgentInteraction`: this says
 * "who did what to whom, when", never message or status TEXT. It is a picture
 * of interaction shape over time, not a transcript.
 */
export const PersistentAgentTimelineSchema = z.object({
    since: z.string().datetime(),
    until: z.string().datetime(),
    /**
     * How far playback actually reaches. Equals `until` normally; when
     * `truncated` it is the timestamp of the last frame included, so the console
     * can scrub honestly to where the data stops instead of implying the tail was
     * quiet.
     */
    effectiveUntil: z.string().datetime(),
    /** True when the window held more than `limit` frames. */
    truncated: z.boolean(),
    /** The state playback starts from — threads, groups and memory as of `since`,
     *  with no edges (they accumulate as the movie plays). */
    snapshot: PersistentAgentInteractionGraphSnapshotSchema,
    /** Chronological, oldest first — the opposite of the newest-first list
     *  endpoints, because this is played forward rather than paged back. */
    frames: z.array(PersistentAgentTimelineFrameSchema),
    /** Frame count per source, for the console's "what am I looking at" caption.
     *  Keyed by frame `kind`. */
    counts: z.record(z.string(), z.number().int().nonnegative()),
    /**
     * Thread id → display name, for threads the frames only reference by id.
     * The interaction log records ids (it is an edge log), and a replay has no
     * later snapshot to fill names in the way a live reconnect does — so without
     * this a thread that started inside the window would stay anonymous on the
     * canvas. Names are current, not historical: the log does not version them.
     * Optional so an older server's response still parses.
     */
    threadNames: z.record(z.string(), z.string()).optional(),
});
// ─── Identity helpers (shared by api + host) ─────────────────────────────
/**
 * The Mastra `resourceId` for an agent — org/agent/thread scoped so an agent
 * can never read another tenant's (or another agent's) thread. Keep api + host
 * using this one function so the scoping can't drift.
 *
 * NOTE: the literal wire format (including the `base:` segment) is
 * intentionally unchanged from before the PersistentAgentGroup ->
 * OrchestrationAgentConfig refactor — existing live threads' Mastra memory is
 * keyed by this string, and changing the format would orphan it. Only the
 * `agentId` parameter's origin changed (it's the orchestration Agent's own id
 * now, not a separate "base agent" it merely referenced).
 */
export function persistentAgentResourceId(orgId, agentId, persistentAgentId) {
    return `org:${orgId}:base:${agentId}:agent:${persistentAgentId}`;
}
/** The Mastra `threadId` for an agent — the agent id IS the thread. */
export function persistentAgentThreadId(persistentAgentId) {
    return persistentAgentId;
}
/**
 * Build the provenance `source` string for an event from a typed origin.
 * Centralized so the watch UI's chip parsing stays consistent.
 */
export function persistentAgentEventSource(origin) {
    switch (origin.type) {
        case 'user':
            return `user:${origin.accountId}`;
        case 'system':
            return `system:${origin.producer}`;
        case 'timer':
            return `timer:${origin.kind ?? 'self'}`;
        case 'signal':
            return `signal:${origin.kind ?? 'trigger'}`;
        case 'agent':
            return `agent:${origin.persistentAgentId}`;
        case 'webhook':
            return `webhook:${origin.provider}`;
    }
}
// ─── Elicitations — the PA HITL primitive (DEV-16877) ──────────────────────
/** Which answer affordances the UI may offer for an elicitation. */
export const PersistentAgentElicitationCapabilitiesSchema = z.object({
    /** Approve — pick a predefined option (or the default action). */
    accept: z.boolean().default(true),
    /** Approve with modifications — `payload` carries the edited value. */
    edit: z.boolean().default(false),
    /** Free-form text answer. */
    respond: z.boolean().default(true),
    /** Dismiss without a decision — the agent proceeds with its judgment. */
    ignore: z.boolean().default(true),
});
/** One predefined choice on an elicitation. */
export const PersistentAgentElicitationOptionSchema = z.object({
    id: z.string().min(1).max(100),
    label: z.string().min(1).max(300),
    description: z.string().max(2_000).optional(),
});
export const PersistentAgentElicitationStatusSchema = z.enum([
    'PENDING',
    'ANSWERED',
    'EXPIRED',
    'CANCELLED',
]);
/** The human's decision on an elicitation. */
export const PersistentAgentElicitationDecisionSchema = z.object({
    action: z.enum(['accept', 'edit', 'respond', 'ignore']),
    /** The chosen predefined option (accept with options). */
    optionId: z.string().optional(),
    /** Free-form answer text (respond) or an edit note. */
    text: z.string().max(20_000).optional(),
    /** Structured payload (edit; validated against responseSchema when set). */
    payload: z.unknown().optional(),
});
/** A persisted elicitation row. */
export const PersistentAgentElicitationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    persistentAgentId: z.string(),
    turnId: z.string().nullable(),
    question: z.string(),
    /** Snapshotted from Agent.elicitationMode at creation time — see the
     *  schema doc comment; not a live reference. (DEV-16877 follow-up) */
    blocking: z.boolean(),
    options: z.array(PersistentAgentElicitationOptionSchema),
    responseSchema: z.unknown().nullable(),
    capabilities: PersistentAgentElicitationCapabilitiesSchema,
    defaultAction: z.string().nullable(),
    expiresAt: z.date().nullable(),
    status: PersistentAgentElicitationStatusSchema,
    decision: PersistentAgentElicitationDecisionSchema.nullable(),
    decidedBySubject: z.string().nullable(),
    decidedAt: z.date().nullable(),
    createdAt: z.date(),
});
/**
 * A PENDING elicitation as listed org-wide for the Inbox (`GET
 * v1/persistent-agent-elicitations/pending`) — the elicitation row plus the
 * agent name, since that view spans every agent in the org rather than one
 * agent's own list. Deliberately NOT part of the `InboxRow` union in
 * `inbox.ts` — that union backs the unconsumed `/v1/inbox` endpoint; this is
 * a standalone type for the dedicated elicitation endpoint, same as HITL and
 * Signal each moved off the unified endpoint to their own.
 */
export const PersistentAgentElicitationInboxRowSchema = PersistentAgentElicitationSchema.extend({
    /** The base (group) Agent id — the `:id` segment the answer/cancel routes
     *  need alongside `persistentAgentId` (the `:agentId`/thread segment). */
    agentId: z.string(),
    persistentAgentName: z.string(),
});
/** Body for `POST .../elicitations/:elicitationId/answer`. */
export const AnswerPersistentAgentElicitationInputSchema = PersistentAgentElicitationDecisionSchema;
/** The agent-side `request_human_input` tool's input (mirrored here so the
 *  host, API, and any future MCP surface agree on one shape). */
export const RequestHumanInputSchema = z.object({
    question: z.string().min(1).max(10_000),
    options: z.array(PersistentAgentElicitationOptionSchema).max(20).optional(),
    responseSchema: z.unknown().optional(),
    capabilities: PersistentAgentElicitationCapabilitiesSchema.partial().optional(),
    /** Seconds until the elicitation expires (the tick sweep wakes you with
     *  defaultAction). Omit to wait indefinitely. */
    expiresInSeconds: z
        .number()
        .int()
        .min(30)
        .max(30 * 24 * 3600)
        .optional(),
    defaultAction: z.string().max(4_000).optional(),
});
// ─── Run-reflection-now (principle judge / reflection ops) ──────────────────
/**
 * Result of forcing a reflection pass for a base agent
 * (`POST /v1/persistent-agent-groups/:agentId/reflection/run`). The action
 * bumps each live thread's ACTIVE `reflection` heartbeat to fire on the signal
 * tick's next sweep (delivered when-idle, exactly like a scheduled beat —
 * same prompt, same marker semantics). Paused beats are respected, not
 * force-fired. `nudged: 0` with `liveThreads > 0` means no thread carries a
 * reflection heartbeat yet — it is seeded when a reflection-enabled
 * supervisor starts (or continues) an operation.
 */
export const RunReflectionNowResultSchema = z.object({
    /** Reflection heartbeats whose next fire was moved to now. */
    nudged: z.number().int().nonnegative(),
    /** Reflection heartbeats left alone because the operator paused them. */
    pausedSkipped: z.number().int().nonnegative(),
    /** Live (non-STOPPED/FAILED) threads of the base agent that were inspected. */
    liveThreads: z.number().int().nonnegative(),
});
//# sourceMappingURL=persistent-agent.js.map